import { ApolloError, type OperationVariables, type QueryRef, useQueryRefHandlers } from '@apollo/client';
import { useRouter } from '@tanstack/react-router';
import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { isRoutePreloadRef } from './routePreload';

type RouteQueryErrorProps = Readonly<{ error: ApolloError; retry: () => void }>;

type RouteQueryBoundaryProps<TData, TVariables> = Readonly<{
  queryRef: QueryRef<TData, TVariables>;
  /** The page's existing loading skeleton. */
  fallback: ReactNode;
  /** The page's existing error view; `retry` reloads the query. */
  errorFallback: (props: RouteQueryErrorProps) => ReactNode;
  children: ReactNode;
}>;

export function RouteQueryBoundary<TData, TVariables extends OperationVariables>({
  queryRef,
  fallback,
  errorFallback,
  children,
}: RouteQueryBoundaryProps<TData, TVariables>) {
  const router: ReturnType<typeof useRouter> | undefined = useRouter({ warn: false });
  const { refetch } = useQueryRefHandlers(queryRef);

  return (
    <ErrorBoundary
      resetKeys={[queryRef]}
      fallbackRender={({ error, resetErrorBoundary }) => {
        if (!(error instanceof ApolloError)) throw error;
        const retry = () => {
          if (router !== undefined && isRoutePreloadRef(queryRef)) {
            void router.invalidate();
            return;
          }
          refetch().catch(() => {});
          resetErrorBoundary();
        };
        return errorFallback({ error, retry });
      }}
    >
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  );
}
